// Tutor AttendancePage — read-only view of a session's attendance.
// Check-in itself happens on the Roster page.
import { useMemo, useState } from "react";
import { useFetch } from "../../hooks/useApi.js";
import { useCourses } from "../../contexts/CoursesContext.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import Badge from "../../components/shared/Badge.js";
import type { AttendanceRecordWithStudent } from "@dm-lms/shared/types";

export default function AttendancePage() {
  const { courses: myCourses } = useCourses();

  const [courseId, setCourseId] = useState("");
  const [sessionId, setSessionId] = useState("");

  const { sessions: allSessions } = useSessions();
  const sessions = useMemo(
    () => (courseId ? allSessions.filter((session) => session.courseId === courseId) : []),
    [allSessions, courseId]
  );
  const { data: attendance, isLoading, error } = useFetch<AttendanceRecordWithStudent[]>(
    sessionId ? `/api/attendance/session/${sessionId}` : null
  );

  function selectCourse(id: string) {
    setCourseId(id);
    setSessionId("");
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Attendance</h1>

      <label className="block text-xs text-dm-slate mb-1">Course</label>
      <select
        className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm mb-6"
        value={courseId}
        onChange={(e) => selectCourse(e.target.value)}
      >
        <option value="">Select a course</option>
        {myCourses.map((course) => (
          <option key={course.id} value={course.id}>{course.code} — {course.title}</option>
        ))}
      </select>

      {courseId && (
        <div className="mb-8">
          <label className="block text-xs text-dm-slate mb-1">Session</label>
          <select
            className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
          >
            <option value="">Select a session</option>
            {(sessions ?? []).map((session) => (
              <option key={session.id} value={session.id}>
                {session.title} — {new Date(session.scheduledAt).toLocaleString()}
              </option>
            ))}
          </select>
        </div>
      )}

      {!courseId && <p className="text-dm-slate text-sm">Select a course to view attendance.</p>}
      {courseId && !sessionId && <p className="text-dm-slate text-sm">Select a session to view attendance.</p>}

      {sessionId && (
        <>
          {isLoading && <p className="text-dm-slate text-sm">Loading…</p>}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!isLoading && !error && (
            (attendance ?? []).length === 0 ? (
              <p className="text-dm-slate text-sm">No attendance records for this session yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-dm-slate">
                    <tr>
                      <th className="px-4 py-3 font-medium">Student</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Checked in</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(attendance ?? []).map((record) => (
                      <tr key={record.id}>
                        <td className="px-4 py-3">
                          {record.studentName}{" "}
                          <span className="text-dm-slate">({record.studentCode})</span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={record.status === "present" ? "green" : record.status === "excused" ? "gold" : "red"}>
                            {record.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {record.checkedInAt ? new Date(record.checkedInAt).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
